import React, { useMemo, useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  CardElement,
} from "@stripe/react-stripe-js";

import ModalSkeleton from "../ModalSkeleton/ModalSkeleton";
import { Col, Container, Row } from "react-bootstrap";
import classes from "./PaymentModal.module.css";
import MailIcon from "@mui/icons-material/Mail";
import Button from "../Button/Button";
import Input from "../Input";
import {
  apiHeader,
  BaseURL,
  falsyArray,
  formRegEx,
  formRegExReplacer,
  validateEmail,
} from "../../config/apiUrl";
import { toast } from "react-toastify";
import { Patch, Post } from "../../Axios/AxiosFunctions";
import { useSelector } from "react-redux";

const PaymentModal = ({ show, setShow, setCardRequestsData, data }) => {
  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();
  const token = useSelector((state) => state?.authReducer?.access_token);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState({
    country: "",
    state: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    postCode: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  //   handleSubmit
  const handleSubmit = async () => {
    if (isLoading) return;

    const params = {
      id: data?._id,
      firstName,
      lastName,
      email,
      telephone,
      ...deliveryAddress,
    };

    delete params["addressLine2"];

    // validate params
    for (let key in params)
      if (falsyArray.includes(params[key]))
        return toast.warn(
          `${key.replace(formRegEx, formRegExReplacer)} can not be empty.`
        );

    params.addressLine2 = deliveryAddress?.addressLine2;

    // validate email
    if (!validateEmail(email))
      return toast.warn("Please enter a valid email address.");

    // check if stripe has loaded
    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });
    setIsLoading(false);
    // if error
    if (payload?.error?.message)
      return toast.warn(`${payload?.error?.message}`);
    setIsLoading(false);

    const finalParams = {
      ...params,
      pmId: payload?.paymentMethod?.id,
    };

    const url = BaseURL("cards/pay");
    setIsLoading(true);
    const response = await Patch(url, finalParams, apiHeader(token));
    setIsLoading(false);
    if (response !== undefined) {
      toast.success("Payment made successfully.");

      setCardRequestsData((p) => {
        let oldData = [...p];
        let currentIndex = oldData.findIndex((e) => e?._id === data?._id);
        oldData[currentIndex].status = "paid";
        return oldData;
      });
      setShow(false)
      // setCardRequestsData
    }
  };

  return (
    <ModalSkeleton
      modalWidth={"58%"}
      modalPadding={"60px"}
      show={show}
      setShow={setShow}
    >
      <Container>
        <h4>My Details</h4>
        <hr />
        <Row className={classes?.myDetails}>
          <Col md={6}>
            <Input
              placeholder={"First Name"}
              value={firstName}
              setter={setFirstName}
              label={"First Name"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Last Name"}
              value={lastName}
              setter={setLastName}
              label={"Last Name"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Email"}
              value={email}
              setter={setEmail}
              label={"Email"}
              type="email"
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Telephone"}
              value={telephone}
              setter={setTelephone}
              label={"Telephone"}
            />
          </Col>
        </Row>
        {/* Delivery Detail */}
        <h4>Delivery Address</h4>
        <hr />
        <Row className={classes?.myDetails}>
          <Col md={6}>
            <Input
              placeholder={"Country"}
              value={deliveryAddress?.country}
              setter={(e) => setDeliveryAddress((p) => ({ ...p, country: e }))}
              label={"Country"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"State"}
              value={deliveryAddress?.state}
              setter={(e) => setDeliveryAddress((p) => ({ ...p, state: e }))}
              label={"State"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Address Line 1"}
              value={deliveryAddress?.addressLine1}
              setter={(e) =>
                setDeliveryAddress((p) => ({ ...p, addressLine1: e }))
              }
              label={"Address Line 1"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Address Line 2"}
              value={deliveryAddress?.addressLine2}
              setter={(e) =>
                setDeliveryAddress((p) => ({ ...p, addressLine2: e }))
              }
              label={"Address Line 2"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Town"}
              value={deliveryAddress?.town}
              setter={(e) => setDeliveryAddress((p) => ({ ...p, town: e }))}
              label={"Town"}
            />
          </Col>
          <Col md={6}>
            <Input
              placeholder={"Post Code"}
              value={deliveryAddress?.postCode}
              setter={(e) => setDeliveryAddress((p) => ({ ...p, postCode: e }))}
              label={"Post Code"}
            />
          </Col>
        </Row>
        <h4>Card Detail</h4>
        <hr />
        <Row>
          <form className={["w-100"].join(" ")}>
            <Col lg={12} md={3} sm={12}>
              <label className={classes.cardNumber}>
                Card number
                <CardNumberElement options={options} />
              </label>
            </Col>
            <Row>
              <Col lg={6} md={6} sm={12}>
                <label className={classes.cardNumber}>
                  Expiration date
                  <CardExpiryElement options={options} />
                </label>
              </Col>
              <Col lg={6} md={6} sm={12}>
                <label className={classes.cardNumber}>
                  CVC
                  <CardCvcElement options={options} />
                </label>
              </Col>
            </Row>
          </form>
        </Row>

        <Row className={classes.input_mar}>
          <div className={classes.lgn_btn_div}>
            <Button primary onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? "Please Wait..." : "Pay Now"}
            </Button>
          </div>
        </Row>
      </Container>
    </ModalSkeleton>
  );
};

export default PaymentModal;

const useOptions = () => {
  const options = useMemo(
    () => ({
      style: {
        base: {
          color: "#424770",
          letterSpacing: "0.025em",
          fontFamily: "Source Code Pro, monospace",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    }),
    []
  );

  return options;
};
